import { Loader2, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { readPublicError } from '@/api/error';
import {
  ExecutionDetails,
  type ExecutionResponseData,
} from '@/components/monitor/execution-details';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface MonitorResponse {
  name?: string;
  url?: string;
  rrule?: string;
}

interface TestResult {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  response_data: ExecutionResponseData | null;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
}

type FormState = {
  name: string;
  url: string;
  frequency: number;
};

const DEFAULT_FORM: FormState = {
  name: '',
  url: '',
  frequency: 5,
};

const rruleToMinutes = (rrule: string | null | undefined) => {
  if (!rrule) return DEFAULT_FORM.frequency;
  const match = rrule.match(/INTERVAL=(\d+)/);
  if (match) {
    const interval = Number.parseInt(match[1], 10);
    if (!Number.isNaN(interval) && interval > 0) {
      return interval;
    }
  }
  return DEFAULT_FORM.frequency;
};

const minutesToRrule = (minutes: number) =>
  `FREQ=MINUTELY;INTERVAL=${Math.max(1, Math.floor(minutes))}`;

export function EditMonitorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const monitorId = searchParams.get('id');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formState, setFormState] = useState<FormState>(DEFAULT_FORM);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!monitorId) {
      setError('Missing monitor identifier.');
      setIsLoading(false);
      return;
    }

    // Validate ID is a valid number
    const parsedId = Number.parseInt(monitorId, 10);
    if (Number.isNaN(parsedId)) {
      setError('Invalid monitor identifier.');
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();

    const loadMonitor = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`/api/monitors/${monitorId}`, {
          credentials: 'include',
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(await readPublicError(response, 'Failed to load monitor'));
        }

        const data = (await response.json()) as MonitorResponse;
        setFormState({
          name: data.name ?? DEFAULT_FORM.name,
          url: data.url ?? DEFAULT_FORM.url,
          frequency: rruleToMinutes(data.rrule),
        });
        setError(null);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        console.error('Failed to fetch monitor:', err);
        setError(err instanceof Error ? err.message : 'Failed to load monitor');
      } finally {
        setIsLoading(false);
      }
    };

    loadMonitor();

    return () => controller.abort();
  }, [monitorId]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!monitorId || Number.isNaN(Number.parseInt(monitorId, 10))) {
      setError('Invalid monitor identifier.');
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      const updateDetailsResponse = await fetch(`/api/monitors/${monitorId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: formState.name,
          url: formState.url,
        }),
      });

      if (!updateDetailsResponse.ok) {
        throw new Error(await readPublicError(updateDetailsResponse, 'Failed to update monitor'));
      }

      const updateScheduleResponse = await fetch(`/api/monitors/${monitorId}/schedule`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          rrule: minutesToRrule(formState.frequency),
        }),
      });

      if (!updateScheduleResponse.ok) {
        throw new Error(await readPublicError(updateScheduleResponse, 'Failed to update schedule'));
      }

      navigate('/dashboard/monitors');
    } catch (err) {
      console.error('Failed to update monitor:', err);
      setError(err instanceof Error ? err.message : 'Failed to update monitor');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (field === 'frequency') {
      const nextValue = Number(event.target.value);
      setFormState((prev) => ({
        ...prev,
        frequency: Number.isNaN(nextValue) ? prev.frequency : Math.max(1, Math.floor(nextValue)),
      }));
      return;
    }

    setFormState((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleTest = async () => {
    if (!monitorId || Number.isNaN(Number.parseInt(monitorId, 10))) {
      setError('Invalid monitor identifier.');
      return;
    }

    setIsTesting(true);
    setTestResult(null);
    setError(null);

    try {
      // Queue the test
      const queueResponse = await fetch(`/api/monitors/${monitorId}/test`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!queueResponse.ok) {
        throw new Error(await readPublicError(queueResponse, 'Failed to start test'));
      }

      const queueResult = (await queueResponse.json()) as { id: string };
      const executionId = queueResult.id;

      // Start polling for results
      let pollCount = 0;
      const maxPolls = 20; // 60 seconds with 3-second intervals

      pollingInterval.current = setInterval(async () => {
        pollCount++;

        try {
          const statusResponse = await fetch(`/api/monitors/execution/${executionId}`, {
            credentials: 'include',
          });

          if (!statusResponse.ok) {
            throw new Error('Failed to check test status');
          }

          const status = (await statusResponse.json()) as TestResult;
          setTestResult(status);

          // Stop polling if test is complete or we've hit the max polls
          if (
            status.status === 'completed' ||
            status.status === 'failed' ||
            pollCount >= maxPolls
          ) {
            if (pollingInterval.current) {
              clearInterval(pollingInterval.current);
              pollingInterval.current = null;
            }
            setIsTesting(false);

            if (
              pollCount >= maxPolls &&
              status.status !== 'completed' &&
              status.status !== 'failed'
            ) {
              setError('Test timed out after 60 seconds');
            }
          }
        } catch (err) {
          console.error('Polling error:', err);
          if (pollCount >= maxPolls) {
            if (pollingInterval.current) {
              clearInterval(pollingInterval.current);
              pollingInterval.current = null;
            }
            setIsTesting(false);
            setError('Failed to retrieve test results');
          }
        }
      }, 3000); // Poll every 3 seconds
    } catch (err) {
      console.error('Test error:', err);
      setIsTesting(false);
      setError(err instanceof Error ? err.message : 'Failed to run test');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b">
            <h1 className="text-xl font-semibold text-gray-900">Edit Monitor</h1>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <div>
              <Label htmlFor="name">Monitor Name</Label>
              <Input
                id="name"
                name="name"
                placeholder="My Website"
                required
                className="mt-1"
                value={formState.name}
                onChange={handleChange('name')}
              />
            </div>

            <div>
              <Label htmlFor="url">URL to Monitor</Label>
              <Input
                id="url"
                name="url"
                type="url"
                placeholder="https://example.com"
                required
                className="mt-1"
                value={formState.url}
                onChange={handleChange('url')}
              />
            </div>

            <div>
              <Label htmlFor="frequency">Probe Frequency (minutes)</Label>
              <Input
                id="frequency"
                name="frequency"
                type="number"
                min="1"
                required
                className="mt-1"
                value={formState.frequency}
                onChange={handleChange('frequency')}
              />
            </div>

            <div className="flex flex-wrap gap-3 pt-4 border-t">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard/monitors')}
              >
                Cancel
              </Button>
              <Button type="button" variant="secondary" onClick={handleTest} disabled={isTesting}>
                {isTesting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Test Monitor
                  </>
                )}
              </Button>
            </div>

            {testResult && (
              <div className="mt-4">
                {testResult.status === 'completed' && testResult.response_data ? (
                  <ExecutionDetails data={testResult.response_data} />
                ) : testResult.status === 'failed' ? (
                  <Alert variant="destructive">
                    <AlertDescription>
                      Test failed: {testResult.error_message || 'Unknown error occurred'}
                    </AlertDescription>
                  </Alert>
                ) : testResult.status === 'processing' || testResult.status === 'pending' ? (
                  <div className="flex items-center gap-2 p-4 border rounded-lg">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                    <span className="text-sm text-gray-600">
                      {testResult.status === 'processing' ? 'Running test...' : 'Test queued...'}
                    </span>
                  </div>
                ) : null}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
